<template>
  <v-dialog
    :value="value"
    @input="v => $emit('input', v)"
    transition="dialog-top-transition"
    scrollable
    width="44em"
    :persistent="!!loading || isAnonymous"
    @click:outside="closeDialog"
    @close="closeDialog"
  >
    <v-card elevation="0">
      <v-card-title class="headline font-weight-bold mb-0 pb-0">
        Barsammlung eintragen
      </v-card-title>
      <v-card-subtitle class="pb-0">
        <v-card
          flat
          class="mt-8 px-0" v-if="isAnonymous"
        >
          <v-card-title class="px-0">{{ anonymousCollectionName }}</v-card-title>
          <v-card-text class="px-0">{{ anonymousOrganizationName }}</v-card-text>
        </v-card>

        <collection-card
          v-if="!isAnonymous"
          class="mt-8"
          :collection="collection"
          :current-organization="organization"
          :show-buttons="false"
        />
        <h5>Zeitpunkt der Barsammlung</h5>
        <v-datetime-picker
          v-model="timestamp"
          :okText="$tc('action.confirm')"
          :clearText="$tc('action.cancel')"
          :disabled="!!loading || !!editCashPayment"
          label="Zeitpunkt"
          :time-picker-props="{format: '24hr'}"
          dateFormat="dd.MM.yyyy,"
          timeFormat="HH:mm"
          :text-field-props="{dense: true, outlined: true, errorMessages: timestamp ? null : 'Dieses Feld ist erforderlich.'}"
        >
          <template v-slot:dateIcon>
            <v-icon>{{ mdiCalendarMonth }}</v-icon>
          </template>
          <template v-slot:timeIcon>
            <v-icon>{{ mdiClockTimeFourOutline }}</v-icon>
          </template>
        </v-datetime-picker>
      </v-card-subtitle>
      <v-card-text>
        <h5 class="mt-0">Bareinnahmen</h5>
        <v-tabs v-model="inputTypeTab">
          <v-tab><v-icon class="mr-2">{{ mdiFormTextbox }}</v-icon> Summe</v-tab>
          <v-tab><v-icon class="mr-2">{{ mdiTableLarge }}</v-icon> Rechner</v-tab>
        </v-tabs>
        <v-tabs-items v-model="inputTypeTab">
          <v-tab-item>
            <CurrencyInput
              class="mt-2"
              v-model="amountCents"
              :disabled="!!loading"
              :currency-input="collectionCurrency"
              outlined
              autofocus
            />
          </v-tab-item>
          <v-tab-item>
            <CashPaymentTableInput
              v-model="amountCents"
              :currency-input="collectionCurrency"
              :disabled="!!loading"
              :cash-split-input="cashSplitInput"
              @cash-step-values-changed="cashStepValues = $event"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!isAnonymous"
          @click.stop.prevent="closeDialog"
          color="neutral"
          :disabled="!!loading"
        >
          Abbrechen
        </v-btn>
        <v-btn
          @click.stop.prevent="delegateSaveCashCollection()"
          color="primary"
          :disabled="amountCents <= 0 || !timestamp"
          :loading="!!loading"
        >
          {{ amountCents | currency(currencySymbol) }}
          eintragen
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import CashPaymentTableInput from '@/components/collection/CashPaymentTableInput.vue'
import CurrencyInput from '@/components/CurrencyInput.vue'
import {
  SET_ERROR_MESSAGE,
  SET_SNACKBAR_MESSAGE,
} from '@/modules/common/store/mutation-types'
import { mdiCalendarMonth, mdiClockTimeFourOutline, mdiFormTextbox, mdiTableLarge } from '@mdi/js'
import { loadingStateWrapper } from '@/modules/common/store/tools'
import { mapState } from 'vuex'
import { ADD_CASH_PAYMENT, EDIT_CASH_PAYMENT } from "@/store/mutation-types"

export default {
  name: "AddCashCollectionDialog",
  emits: [
    'close',
    'save-cash-collection',
    'payment-success'
  ],
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    collection: {
      type: Object,
      required: false,
    },
    organization: {
      type: Object,
      required: false,
    },
    anonymousCollectionName: {
      type: String,
      required: false,
    },
    anonymousOrganizationName: {
      type: String,
      required: false,
    },
    currencyInput: {
      type: String,
      required: false,
      default: 'EUR',
    },
    editCashPayment: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data: () => ({
    inputTypeTab: null,
    amountCents: 0,
    timestamp: new Date(),
    cashStepValues: [],
    cashSplitInput: {},
    mdiCalendarMonth,
    mdiClockTimeFourOutline,
    mdiFormTextbox,
    mdiTableLarge,
  }),
  components: {
    CashPaymentTableInput,
    CurrencyInput,
  },
  beforeCreate () {
    // ref: https://v2.vuejs.org/v2/guide/components-edge-cases.html?redirect=true#Circular-References-Between-Components
    this.$options.components.CollectionCard = require('@/components/collection/CollectionCard.vue').default
  },
  computed: {
    ...mapState(['loading']),
    isAnonymous () {
      return !this.organization && !this.collection
    },
    collectionCurrency () {
      if (this.isAnonymous && this.currencyInput) {
        return this.currencyInput
      }
      return this.organization?.currency || 'EUR'
    },
    currencySymbol () {
      if (!this.organization) {
        return '€'
      }
      switch (this.organization?.currency) {
        case 'EUR':
          return '€'
        case 'CHF':
          return ' CHF'
        default:
          return this.organization?.currency || ''
      }
    }
  },
  mounted () {
  },
  methods: {
    async fetchPendingEnteredPayments () {
      // TODO
    },
    closeDialog () {
      this.$emit('close')
      this.$emit('input', false)
      this.reset()
    },
    reset () {
      this.amountCents = 0
      this.timestamp = new Date()
      this.cashStepValues = []
      this.cashSplitInput = {}
    },
    async delegateSaveCashCollection () {
      if (this.amountCents <= 0 || !this.timestamp) {
        return
      }

      if (this.isAnonymous) {
        this.saveCashCollectionAnonymous()
      } else {
        this.saveCashCollection()
      }

      this.closeDialog()
    },
    async saveCashCollection () {
      const amount = this.$options.filters.currency(this.amountCents, this.currencySymbol)
      const message = `Sie haben erfolgreich eine Barsammlung in der Höhe von ${amount} am ${this.timestamp.toLocaleDateString()} eingetragen. Vielen Dank`
      let response = null
      const cashSplit = {
        cash: {
          split: {}
        }
      }
      this.cashStepValues.forEach(({ value, input }) => {
        cashSplit.cash.split[value] = input ? parseInt(input, 10) : 0
      })
      await loadingStateWrapper(this.$store, async () => {
        try {
          const payload = {
            amount: '' + (this.amountCents / 100).toFixed(2),
            plan: this.collection.url,
            organization: this.organization.url,
            collection_datetime: this.timestamp,
          }

          if (Object.keys(cashSplit).length > 0) {
            payload.meta = cashSplit
          }
          if (this.editCashPayment) {
            response = await this.$store.getters.restApi.patch(this.editCashPayment.url, payload)
            this.$store.commit('payment/' + EDIT_CASH_PAYMENT, { cashPayment: response.data })
          } else {
            response = await this.$store.getters.restApi.post('cash/cash_payment/', payload)
            this.$store.commit('payment/' + ADD_CASH_PAYMENT, { cashPayment: response.data })
          }

          if ([200, 201].includes(response.status)) {
            this.$emit('payment-success', response.data.amount)
            await this.$store.commit(
              SET_SNACKBAR_MESSAGE,
              { message },
              { root: true }
            )
          } else {
            throw new Error('Die Barsammlung konnte nicht eingetragen werden.')
          }
        } catch (e) {
          await this.$store.commit(
            SET_ERROR_MESSAGE,
            { message: 'Die Barsammlung konnte nicht eingetragen werden.' },
            { root: true }
          )
        }
      })
    },
    async saveCashCollectionAnonymous () {
      const cashSplit = {
        cash: {
          split: {}
        }
      }
      this.cashStepValues.forEach(({ value, input }) => {
        // Use this formatting to appease the API specification
        const key = `${(value / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false })} ${this.currencySymbol}`
        cashSplit.cash.split[key] = input ? parseInt(input, 10) : 0
      })

      await loadingStateWrapper(this.$store, async () => {
        const amount = '' + (this.amountCents / 100).toFixed(2)
        this.$emit('save-cash-collection', {
          amount,
          cashSplit
        })
      })
    },
  },
  watch: {
    async value (newVal) {
      if (!newVal) {
        return
      }

      await this.fetchPendingEnteredPayments()
    },
    editCashPayment (newVal) {
      if (newVal) {
        this.amountCents = newVal.amount * 100
        this.timestamp = new Date(newVal.created_at)
        this.cashSplitInput = newVal.meta?.cash?.split || {}
        this.inputTypeTab = Object.keys(this.cashSplitInput).length > 0 ? 1 : 0
      } else {
        this.inputTypeTab = 0
      }
    },
  }
}
</script>

<style scoped>

</style>
